import { api } from '@udm/contracts';
import { useQuery } from '@tanstack/react-query';
import { Check, Minus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { request } from '../../api/client';
import { Card } from '../../ui/components';
import { formatDateTime } from '../../ui/format';

const KIND_ORDER = ['control', 'query', 'configure', 'telemetry', 'reply'];

function Flag({ on, label }: { on: boolean; label: string }) {
  return on ? (
    <Check size={14} aria-label={label} className="status-online" />
  ) : (
    <Minus size={14} aria-label={label} className="muted" />
  );
}

/**
 * What the gateway serving the device declares for its protocol (GET …/functions): per function, vendor support,
 * implemented, tested offline, tested live and whether it is enabled — or blocked (refused unless enabled for one
 * named device). Informative: what users may run is decided by Central's own capability catalog.
 */
export function FunctionsCard({ id }: { id: string }) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ['device', id, 'functions'],
    queryFn: () =>
      request('GET', `/api/v1/devices/${id}/functions`, undefined, (v) => api.DeviceFunctions.parse(v)),
    staleTime: 10 * 60_000,
  });
  const d = q.data;
  if (!d || !d.items.length) return null;
  const items = [...d.items].sort(
    (a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind) || a.function.localeCompare(b.function),
  );
  const count = (f: (x: api.DeviceFunction) => boolean) => items.filter(f).length;
  const state = (f: api.DeviceFunction) =>
    f.safety === 'blocked'
      ? { cls: 'badge-critical', key: 'blocked' as const }
      : f.operationally_enabled
        ? { cls: 'badge-online', key: 'enabled' as const }
        : f.implemented
          ? { cls: 'badge-unknown', key: 'disabled' as const }
          : { cls: 'badge-unknown', key: 'notImplemented' as const };
  return (
    <Card title={t('devices.functions.title')}>
      <p className="small" style={{ marginTop: 0 }}>
        {t('devices.functions.summary', {
          total: items.length,
          implemented: count((f) => f.implemented),
          live: count((f) => f.tested_live),
          enabled: count((f) => f.operationally_enabled),
          blocked: count((f) => f.safety === 'blocked'),
        })}
      </p>
      <details>
        <summary className="small">{t('devices.functions.show')}</summary>
        <div className="table-scroll" style={{ marginTop: 8 }}>
          <table className="table">
            <thead>
              <tr>
                <th>{t('devices.functions.function')}</th>
                <th>{t('devices.functions.vendor')}</th>
                <th>{t('devices.functions.implemented')}</th>
                <th>{t('devices.functions.offline')}</th>
                <th>{t('devices.functions.live')}</th>
                <th>{t('devices.functions.state')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((f, i) => {
                const s = state(f);
                return (
                  <tr key={`${f.function}-${i}`} title={f.note ?? undefined}>
                    <td style={{ whiteSpace: 'normal', minWidth: 220 }}>
                      {f.function}
                      {f.vendor_ref && <div className="muted small mono">{f.vendor_ref}</div>}
                    </td>
                    <td>
                      {t(
                        `devices.functions.vendorLevel.${f.supported_by_vendor}` as 'devices.functions.vendorLevel.documented',
                        {
                          defaultValue: f.supported_by_vendor,
                        },
                      )}
                    </td>
                    <td>
                      <Flag on={f.implemented} label={t('devices.functions.implemented')} />
                    </td>
                    <td>
                      <Flag on={f.tested_offline} label={t('devices.functions.offline')} />
                    </td>
                    <td>
                      <Flag on={f.tested_live} label={t('devices.functions.live')} />
                    </td>
                    <td>
                      <span className={`badge ${s.cls}`}>{t(`devices.functions.states.${s.key}`)}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="muted small" style={{ marginBottom: 0 }}>
          {t('devices.functions.source', {
            gateway: d.gateway_key ?? '—',
            version: d.catalog_version ?? '—',
            at: formatDateTime(d.received_at ?? undefined),
          })}
        </p>
      </details>
    </Card>
  );
}
